import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useSearchModal } from '@/context/SearchModalContext'
import { useDebounce } from '@/hooks/use-debounce'
import { useGetCasinoGamesQuery } from '@/store/api/casinoApi'
import { useGetOriginalGamesQuery } from '@/store/api/originalApi'
import { CASINO_GAMES_PER_PAGE, SEARCH_MIN_CHARS, SEARCH_RESULTS_LIMIT } from '@/config/search'
import { GamesWorthTrying } from './GamesWorthTrying'
import { SearchEmptyState } from './SearchEmptyState'
import { SearchInput } from './SearchInput'
import { SearchLoadingState } from './SearchLoadingState'
import { SearchResults } from './SearchResults'
import type { SearchResult } from '@/types/search'

export function SearchModal() {
	const { isOpen, close } = useSearchModal()
	const location = useLocation()
	const [searchText, setSearchText] = useState('')
	const debouncedSearch = useDebounce(searchText, 400)
	const trimmedSearch = debouncedSearch.trim()
	const shouldSearch = trimmedSearch.length >= SEARCH_MIN_CHARS

	const {
		data: casinoResponse,
		isFetching: isCasinoFetching,
		isError: isCasinoError,
		isSuccess: isCasinoSuccess,
	} = useGetCasinoGamesQuery(
		{
			search: trimmedSearch,
			page: 1,
			per_page: CASINO_GAMES_PER_PAGE,
		},
		{ skip: !shouldSearch || !isOpen },
	)

	const {
		data: originalResponse,
		isFetching: isOriginalFetching,
		isError: isOriginalError,
		isSuccess: isOriginalSuccess,
	} = useGetOriginalGamesQuery({ search: trimmedSearch }, { skip: !shouldSearch || !isOpen })

	const locationKeyRef = useRef(location.key)

	useEffect(() => {
		if (!isOpen) {
			setSearchText('')
		}
	}, [isOpen])

	useEffect(() => {
		if (locationKeyRef.current === location.key) {
			return
		}

		locationKeyRef.current = location.key
		if (isOpen) {
			close()
		}
	}, [close, isOpen, location.key])

	const results = useMemo<SearchResult[]>(() => {
		if (!shouldSearch) {
			return []
		}

		const casinoItems: SearchResult[] = (casinoResponse?.data ?? []).map((game) => ({
			kind: 'casino',
			id: game.id,
			game,
		}))

		const originalItems: SearchResult[] = (originalResponse?.data ?? []).map((game) => ({
			kind: 'original',
			id: game.id,
			game,
		}))

		return [...originalItems, ...casinoItems].slice(0, SEARCH_RESULTS_LIMIT)
	}, [casinoResponse?.data, originalResponse?.data, shouldSearch])

	const isSearching = shouldSearch && (isCasinoFetching || isOriginalFetching)
	const searchSettled = shouldSearch && !isSearching && (isCasinoSuccess || isCasinoError) && (isOriginalSuccess || isOriginalError)
	const showEmpty = searchSettled && results.length === 0 && !isCasinoError && !isOriginalError

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<Dialog.Portal>
				<Dialog.Overlay className='fixed inset-0 z-[1000] bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
				<Dialog.Content
					className='fixed left-1/2 top-[8%] z-[1000] flex max-h-[min(840px,84vh)] w-[min(1100px,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-6 overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:p-6'
					aria-describedby={undefined}
				>
					<Dialog.Title className='sr-only'>Search games</Dialog.Title>

					<div className='flex items-start gap-3'>
						<SearchInput value={searchText} onChange={setSearchText} autoFocus />
						<Dialog.Close
							className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-secondary text-foreground transition-colors hover:bg-accent'
							aria-label='Close search'
						>
							<X className='h-5 w-5' />
						</Dialog.Close>
					</div>

					<div className='min-h-0 flex-1 overflow-y-auto'>
						{shouldSearch ? (
							<div className='flex flex-col gap-8'>
								{isSearching ? <SearchLoadingState /> : null}
								{!isSearching && results.length > 0 ? <SearchResults items={results} /> : null}
								{showEmpty ? <SearchEmptyState /> : null}
								{(isCasinoError || isOriginalError) && !isSearching && results.length === 0 ? (
									<p className='py-6 text-center text-sm text-muted-foreground'>Unable to search games right now.</p>
								) : null}
								<GamesWorthTrying />
							</div>
						) : (
							<GamesWorthTrying />
						)}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
